import * as angular from 'angular';
import { BehaviorSubject } from 'rxjs';

import { DeprecatedApiService, GroupResource } from '@mode/legacy/data-access-model-js';
import { SelectOption, TableListData, TableListSchema, TableListValueType } from '@mode/shared/ui-angularjs';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import {
  EntitledActionState,
  SelectFieldType,
} from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions-access-dropdown/connection-permissions-access-dropdown.component';
import { ConnectionPermissionsService } from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions.service';
import { AppStore } from '~/types/main.types';

import { RouterLink } from '@mode/shared/ui-angularjs';

interface GroupRecord {
  name: string;
  member_count: string;
}

class ConnectionPermissionsUserDetailController {
  private accessOptions: EntitledActionState[] = [];
  private accountDataSourceEntitlement: any;
  private accountIsAdmin: boolean;
  private accountUserGroups: GroupResource[];
  private groups: GroupResource[];
  private groupRecords$: BehaviorSubject<TableListData<GroupRecord>[]> = new BehaviorSubject([]);
  // @todo make stronger with {@link GroupRouterLink}
  private groupRouterLinks$: BehaviorSubject<Array<RouterLink<any>>> = new BehaviorSubject([]);
  private groupTableListSchema: TableListSchema = {
    fields: [
      {
        name: 'name',
        title: null,
        type: TableListValueType.Text,
      },
      {
        name: 'member_count',
        title: null,
        type: TableListValueType.Text,
      },
    ],
  };
  entitlementAction$: BehaviorSubject<any> = new BehaviorSubject(null);
  workspaceTitle = 'Workspace';

  static $inject = [
    'ToastService',
    'RxStoreService',
    'ApiService',
    'ConnectionPermissionsService',
    '$state',
    '$stateParams',
    '$timeout',
  ];

  constructor(
    private AlertService,
    private rxStore: AppStore,
    private api: DeprecatedApiService,
    private connectionPermissionsService: ConnectionPermissionsService,
    private $state,
    private $stateParams,
    private $timeout
  ) {}

  $onInit() {
    const orgUsername = this.$stateParams.orgUsername;
    const dataSourceToken = this.$stateParams.dataSourceToken;

    this.accessOptions = this.getAccessOptions();
    this.groupRouterLinks$.next(
      this.accountUserGroups.map(({ token }) => this.createGroupLink(orgUsername, dataSourceToken, token))
    );
    this.groupRecords$.next(
      this.accountUserGroups.map(({ name, member_count }) => ({
        values: {
          name,
          member_count: `${member_count} members`,
        },
      }))
    );

    this.entitlementAction$.next(this.accountDataSourceEntitlement.action);
  }

  createGroupLink(orgUsername, dataSourceToken, groupToken): RouterLink<any> {
    if (!orgUsername && !dataSourceToken && !groupToken) {
      return null;
    }

    return {
      stateOrName: 'org-settings.connection.permissions.group-detail',
      params: {
        orgUsername,
        dataSourceToken,
        groupToken,
      },
    };
  }

  private hasAccess(): boolean {
    return this.hasAdminAccess() || this.hasGroupAccess() || this.hasIndividualAccess();
  }

  private hasIndividualAccess(): boolean {
    return !!this.accountDataSourceEntitlement;
  }

  private hasGroupAccess(): boolean {
    return !!this.accountUserGroups.length;
  }

  private hasAdminAccess(): boolean {
    return this.accountIsAdmin;
  }

  alertAndRedirect() {
    // if this user still has access to this connection, via a group
    // then don't redirect them
    if (this.hasAccess()) {
      this.alertSuccess('Removed Individual Access.');
    } else {
      // else redirect them
      this.alertSuccess('Removed Individual Access. Redirecting...');
      this.redirectToPermissionPanel(4000);
    }
  }

  removeUserPermissionAccess() {
    try {
      this.api
        .destroy(this.accountDataSourceEntitlement, {})
        .then(() => {
          if (!this.hasGroupAccess()) {
            // intentional delay to allow BE to sync Algolia index
            this.redirectToPermissionPanel(4000);
            this.connectionPermissionsService.alertAccessRemoved({ redirect: true });
          } else {
            // reload the state to re-call the router resolvers
            // this will update the accountDataSourceGrant property, which we use to determine hasIndividualAccess
            this.$state.reload();
            this.connectionPermissionsService.alertAccessRemoved();
          }
        })
        .catch((err) => {
          BugsnagHelpers.notifySafely(err);
          this.AlertService.error(`Could not remove individual access`);
        });
    } catch (err) {
      BugsnagHelpers.notifySafely(err);
      this.AlertService.error(`Could not remove individual access`);
    }
  }

  alertSuccess(msgTxt) {
    this.AlertService.success(msgTxt);
  }

  redirectToPermissionPanel(delayInMS = 0, params = null) {
    this.$timeout(() => {
      this.$state.go('org-settings.connection.permissions.list', params);
    }, delayInMS);
  }

  handlePermissionUpdate($option: SelectOption) {
    if ($option.value === 'none') {
      return this.removeUserPermissionAccess();
    }
    this.updateEntitlementPermission($option.value.toString());
  }

  displayAdminAccessBadge(): boolean {
    return this.hasAdminAccess();
  }

  displayGroupAccessBadge(): boolean {
    return this.hasGroupAccess();
  }

  displayIndividualAccessBadge(): boolean {
    return this.hasIndividualAccess() && !this.hasAdminAccess();
  }

  displayAccessDropdown(): boolean {
    return this.hasIndividualAccess() && !this.hasAdminAccess();
  }

  updateEntitlementPermission(actionType: string) {
    const editPromise = this.api.edit(this.accountDataSourceEntitlement, {
      'permission.action': actionType,
    });

    this.entitlementAction$.next(actionType);
    editPromise
      .then(() => {
        this.$state.reload();
        this.connectionPermissionsService.alertAccessUpdated();
      })
      .catch((err) => {
        this.entitlementAction$.next(this.accountDataSourceEntitlement.action);
        BugsnagHelpers.notifySafely(err);
        this.AlertService.error(`Could not change user access`);
      });
  }

  getAccessOptions(): EntitledActionState[] {
    return this.connectionPermissionsService.getEntitlementAccessOptions(this.accountDataSourceEntitlement);
  }
}

export const ConnectionPermissionsUserDetailComponent: angular.IComponentOptions = {
  controller: ConnectionPermissionsUserDetailController,
  controllerAs: 'ctrl',
  bindings: {
    accountName: '<',
    accountIsAdmin: '<',
    accountAvatar: '<',
    accountUserGroups: '<',
    accountDataSourceEntitlement: '<',
  },
  template: `
    <div class="permissions-user-detail">
      <div class="tab-section-wrapper">
        <a ui-sref="org-settings.connection.permissions.list">
          &lt; Back to All members
        </a>
      </div>

      <hr />

      <section class="detail-header">
        <span class="user-avatar">
          <mode-avatar options="ctrl.accountAvatar" size="25"></mode-avatar>
        </span>
        <span class="user-name page-name user-detail-page-name">
          {{ctrl.accountName}}
        </span>
        <span
          class="access-badge access-badge--individual"
          ng-if="ctrl.displayIndividualAccessBadge()"
          tooltip-class="tooltip--connection-permissions-user-detail"
          tooltip-placement="bottom"
          uib-tooltip="This member was granted individual access to this connection"
        >
          Individual
        </span>
        <span
          class="access-badge access-badge--group"
          ng-if="ctrl.displayGroupAccessBadge()"
          tooltip-class="tooltip--connection-permissions-user-detail"
          tooltip-placement="bottom"
          uib-tooltip="This member was granted access to this connection through a group"
        >
          Group
        </span>
        <span
          class="access-badge access-badge--admin"
          ng-if="ctrl.displayAdminAccessBadge()"
          tooltip-class="tooltip--connection-permissions-user-detail"
          tooltip-placement="bottom"
          uib-tooltip="This member's Admin status grants access to all {{ ctrl.workspaceTitle }} connections"
        >
          Admin
        </span>
      </section>

      <div class="detail-wrapper user-detail-wrapper">
        <section class="detail-body">
          <div class="user-access-table user-access-individual user-access-individual--simple">
            <div class="individual-access-info">
              <h4 class="access-title">
                Individual Access

                <connection-permissions-access-dropdown
                  ng-if="ctrl.displayAccessDropdown()"
                  field-type="${SelectFieldType.Inline}"
                  on-change="ctrl.handlePermissionUpdate($option)"
                  options="ctrl.accessOptions"
                  remove-enabled="true"
                  value="ctrl.entitlementAction$ | async:this"
                />
                <div ng-if="!ctrl.displayAccessDropdown()" class="individual-access-empty-state">
                  None
                </div>
              </h4>
            </div>
          </div>

        <hr />

          <div class="user-access-table user-access-group">
            <h4 class="access-title">Group Access</h4>

            <table-list
              ng-if="(ctrl.groupRecords$ | async:this).length"
              router-links="(ctrl.groupRouterLinks$ | async:this)"
              schema="ctrl.groupTableListSchema"
              data="ctrl.groupRecords$ | async:this"
            />
            <div ng-if="!(ctrl.groupRecords$ | async:this).length" class="group-access-empty-state">
              None
            </div>
          </div>
        </section>

        <section class="detail-footer">
          <paginator />
        </section>
      </div>
    </div>
  `,
};
